import { FC, useMemo, useState } from 'react'

import { Field, SelectDropdown, Slider } from '@makyoapp/makyo-ui'

import { SectionBackgroundGroupProps } from '@/shared/builder/default/section.default'
import { BrandKitColorsDropdown } from '@/components/BrandKitColorsDropdown'

interface EffectBackgroundSelectProps {
  layer: SectionBackgroundGroupProps
  handleUpdate: (id: number, newValues: any) => Promise<void>
}

const EFFECT_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'gradient', label: 'Gradient' },
  { value: 'animated-gradient', label: 'Animated Gradient' },
  { value: 'beams', label: 'Beams' },
  { value: 'wavy', label: 'Wavy' },
  { value: 'sparkles', label: 'Sparkles' },
  { value: 'flicker-grid', label: 'Flicker Grid' },
  { value: 'meteors', label: 'Meteors' },
  { value: 'stars', label: 'Stars' },
  { value: 'boxes', label: 'Boxes' },
  { value: 'aurora', label: 'Aurora' },
  { value: 'paths', label: 'Paths' },
  { value: 'fluid-gradient', label: 'Fluid Gradient' },
  { value: 'shader-clouds', label: 'Shader Clouds' },
  { value: 'orbs', label: 'Orbs' },
  { value: 'dot-matrix', label: 'Dot Matrix' },
  { value: 'spotlight', label: 'Spotlight' },
  { value: 'noise', label: 'Noise' },
  { value: 'mesh-gradient', label: 'Mesh Gradient' },
  { value: 'ripple-waves', label: 'Ripple Waves' },
  { value: 'floating-bubbles', label: 'Floating Bubbles' },
  { value: 'neural-network', label: 'Neural Network' },
  { value: 'matrix-rain', label: 'Matrix Rain' },
  { value: 'particles-flow', label: 'Particles Flow' },
]

const MultiColorPicker = ({
  label,
  colors,
  onChange,
}: {
  label: string
  colors: string[]
  onChange: (colors: string[]) => void
}) => (
  <Field isVertical>
    <span className='text-left'>{label}</span>
    <div className='space-y-2'>
      {colors.map((color, index) => (
        <div key={index} className='flex items-center gap-2'>
          <div className='flex-1'>
            <BrandKitColorsDropdown
              value={color}
              onChange={(newColor) => {
                const newColors = [...colors]
                newColors[index] = newColor as string
                onChange(newColors)
              }}
              showExtendedColors
            />
          </div>
          {colors.length > 1 && (
            <button
              type='button'
              onClick={() => {
                const newColors = colors.filter((_, i) => i !== index)
                onChange(newColors)
              }}
              className='px-2 py-1 text-red-600 hover:bg-red-50 rounded'
            >
              ×
            </button>
          )}
        </div>
      ))}
      <button
        type='button'
        onClick={() => onChange([...colors, 'primary-40'])}
        className='text-sm text-primary-400 hover:text-primary-500'
      >
        + Add Color
      </button>
    </div>
  </Field>
)

export const EffectBackgroundSelect: FC<EffectBackgroundSelectProps> = ({ layer, handleUpdate }) => {
  const values = layer.values as any
  const effectType = values.effectType || 'none'
  const effectSpeed = values.effectSpeed ?? 100
  const effectOpacity = values.effectOpacity ?? 100
  const effectColors = values.effectColors || ['primary-40', 'secondary-40', 'tertiary-40']
  const effectBackgroundColor = values.effectBackgroundColor || 'neutral-0'

  const [localColors, setLocalColors] = useState<string[]>(effectColors)
  const [localBgColor, setLocalBgColor] = useState<string>(effectBackgroundColor)

  const needsBackgroundColor = useMemo(() => {
    return [
      'beams',
      'wavy',
      'sparkles',
      'flicker-grid',
      'meteors',
      'stars',
      'boxes',
      'aurora',
      'paths',
      'fluid-gradient',
      'shader-clouds',
      'orbs',
      'dot-matrix',
      'spotlight',
      'noise',
      'ripple-waves',
      'floating-bubbles',
      'neural-network',
      'matrix-rain',
      'particles-flow',
    ].includes(effectType)
  }, [effectType])

  const needsMultipleColors = useMemo(() => {
    return [
      'gradient',
      'animated-gradient',
      'beams',
      'wavy',
      'boxes',
      'aurora',
      'paths',
      'fluid-gradient',
      'shader-clouds',
    ].includes(effectType)
  }, [effectType])

  const needsSingleColor = useMemo(() => {
    return [
      'sparkles',
      'flicker-grid',
      'meteors',
      'stars',
      'dot-matrix',
      'ripple-waves',
      'floating-bubbles',
      'neural-network',
      'matrix-rain',
      'particles-flow',
    ].includes(effectType)
  }, [effectType])

  const handleColorChange = (newColors: string[]) => {
    setLocalColors(newColors)
    handleUpdate(layer.id, { effectColors: newColors })
  }

  const handleBgColorChange = (newColor: string) => {
    setLocalBgColor(newColor)
    handleUpdate(layer.id, { effectBackgroundColor: newColor })
  }

  return (
    <div className='space-y-4'>
      <Field isVertical>
        <span className='text-left'>Effect Type</span>
        <SelectDropdown
          id={`effect-type-${layer.id}`}
          value={EFFECT_TYPES.find((opt) => opt.value === effectType)}
          onChange={async (opt) => {
            const newEffectType = opt?.value || 'none'
            await handleUpdate(layer.id, { effectType: newEffectType })
          }}
          options={EFFECT_TYPES}
          optionLabel={(opt) => opt?.label}
          withSearch
        />
      </Field>

      {effectType !== 'none' && (
        <>
          <Field isVertical>
            <span className='text-left'>Animation Speed</span>
            <div className='w-full'>
              <Slider
                id={`effect-speed-${layer.id}`}
                values={[effectSpeed]}
                step={1}
                min={0}
                max={200}
                renderLabel={(v) => `${v}%`}
                renderTrackLabel={(v) => `${v}%`}
                showMark
                onChange={(value) => {
                  handleUpdate(layer.id, { effectSpeed: value[0] })
                }}
              />
            </div>
          </Field>

          <Field isVertical>
            <span className='text-left'>Opacity</span>
            <div className='w-full'>
              <Slider
                id={`effect-opacity-${layer.id}`}
                values={[effectOpacity]}
                step={1}
                min={0}
                max={100}
                renderLabel={(v) => `${v}%`}
                renderTrackLabel={(v) => `${v}%`}
                showMark
                onChange={(value) => {
                  handleUpdate(layer.id, { effectOpacity: value[0] })
                }}
              />
            </div>
          </Field>

          {needsBackgroundColor && (
            <Field isVertical>
              <span className='text-left'>Background Color</span>
              <BrandKitColorsDropdown
                value={localBgColor}
                onChange={(color) => handleBgColorChange(color as string)}
                showExtendedColors
              />
            </Field>
          )}

          {needsMultipleColors && <MultiColorPicker label='Colors' colors={localColors} onChange={handleColorChange} />}

          {needsSingleColor && effectType !== 'none' && (
            <Field isVertical>
              <span className='text-left'>Effect Color</span>
              <BrandKitColorsDropdown
                value={localColors[0] || 'primary-40'}
                onChange={(color) => handleColorChange([color as string])}
                showExtendedColors
              />
            </Field>
          )}

          {effectType === 'spotlight' && (
            <>
              <Field isVertical>
                <span className='text-left'>Spotlight Color 1</span>
                <BrandKitColorsDropdown
                  value={localColors[0] || 'primary-40'}
                  onChange={(color) => {
                    const newColors = [...localColors]
                    newColors[0] = color as string
                    handleColorChange(newColors)
                  }}
                  showExtendedColors
                />
              </Field>
              <Field isVertical>
                <span className='text-left'>Spotlight Color 2</span>
                <BrandKitColorsDropdown
                  value={localColors[1] || 'secondary-40'}
                  onChange={(color) => {
                    const newColors = [...localColors]
                    newColors[1] = (color as string) || 'secondary-40'
                    if (newColors.length < 2) newColors.push('secondary-40')
                    handleColorChange(newColors)
                  }}
                  showExtendedColors
                />
              </Field>
            </>
          )}

          {effectType === 'mesh-gradient' && (
            <>
              <Field isVertical>
                <span className='text-left'>Mesh Color 1</span>
                <BrandKitColorsDropdown
                  value={localColors[0] || 'primary-40'}
                  onChange={(color) => {
                    const newColors = [...localColors]
                    newColors[0] = color as string
                    handleColorChange(newColors)
                  }}
                  showExtendedColors
                />
              </Field>
              <Field isVertical>
                <span className='text-left'>Mesh Color 2</span>
                <BrandKitColorsDropdown
                  value={localColors[1] || 'secondary-40'}
                  onChange={(color) => {
                    const newColors = [...localColors]
                    newColors[1] = (color as string) || 'secondary-40'
                    if (newColors.length < 2) newColors.push('secondary-40')
                    handleColorChange(newColors)
                  }}
                  showExtendedColors
                />
              </Field>
              <Field isVertical>
                <span className='text-left'>Mesh Color 3</span>
                <BrandKitColorsDropdown
                  value={localColors[2] || 'tertiary-40'}
                  onChange={(color) => {
                    const newColors = [...localColors]
                    newColors[2] = (color as string) || 'tertiary-40'
                    if (newColors.length < 3) newColors.push('tertiary-40')
                    handleColorChange(newColors)
                  }}
                  showExtendedColors
                />
              </Field>
              <Field isVertical>
                <span className='text-left'>Mesh Color 4</span>
                <BrandKitColorsDropdown
                  value={localColors[3] || 'neutral-40'}
                  onChange={(color) => {
                    const newColors = [...localColors]
                    newColors[3] = (color as string) || 'neutral-40'
                    if (newColors.length < 4) newColors.push('neutral-40')
                    handleColorChange(newColors)
                  }}
                  showExtendedColors
                />
              </Field>
            </>
          )}

          {effectType === 'orbs' && (
            <>
              <Field isVertical>
                <span className='text-left'>Orb Color 1</span>
                <BrandKitColorsDropdown
                  value={localColors[0] || 'primary-40'}
                  onChange={(color) => {
                    const newColors = [...localColors]
                    newColors[0] = color as string
                    handleColorChange(newColors)
                  }}
                  showExtendedColors
                />
              </Field>
              <Field isVertical>
                <span className='text-left'>Orb Color 2</span>
                <BrandKitColorsDropdown
                  value={localColors[1] || 'secondary-40'}
                  onChange={(color) => {
                    const newColors = [...localColors]
                    newColors[1] = (color as string) || 'secondary-40'
                    if (newColors.length < 2) newColors.push('secondary-40')
                    handleColorChange(newColors)
                  }}
                  showExtendedColors
                />
              </Field>
            </>
          )}

          {effectType === 'neural-network' && (
            <>
              <Field isVertical>
                <span className='text-left'>Node Color</span>
                <BrandKitColorsDropdown
                  value={localColors[0] || 'primary-40'}
                  onChange={(color) => {
                    const newColors = [...localColors]
                    newColors[0] = color as string
                    handleColorChange(newColors)
                  }}
                  showExtendedColors
                />
              </Field>
              <Field isVertical>
                <span className='text-left'>Line Color</span>
                <BrandKitColorsDropdown
                  value={localColors[1] || 'primary-40'}
                  onChange={(color) => {
                    const newColors = [...localColors]
                    newColors[1] = (color as string) || 'primary-40'
                    if (newColors.length < 2) newColors.push('primary-40')
                    handleColorChange(newColors)
                  }}
                  showExtendedColors
                />
              </Field>
            </>
          )}
        </>
      )}
    </div>
  )
}
